import { Container, Form, FormSelectNewOption, Text, UseMelioFormResults } from '@melio/penny';
import { FundingSource } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { keyBy } from 'lodash';
import React from 'react';

import { PaymentFlowFormFields, PaymentFlowOnChangeHandlers } from '../../../../types';
import { FundingSourceCard } from './FundingSourceCard';
import { useFundingSourcesSearchBar } from './useFundingSourceSearchBar';

const ADD_NEW_FS_VALUE = 'ADD_NEW_FS';

type FundingSourceInputProps = {
  form: UseMelioFormResults<PaymentFlowFormFields>;
  onChange: PaymentFlowOnChangeHandlers['onFundingSourceChange'];
  onNewFundingSourceClick: VoidFunction;
  fundingSources: FundingSource[];
  isLoading: boolean;
};

export const FundingSourceInput = ({
  form,
  onChange,
  onNewFundingSourceClick,
  fundingSources,
  isLoading,
}: FundingSourceInputProps) => {
  const { formatMessage } = useMelioIntl();
  const { registerField } = form;

  const indexedFundingSources = keyBy(fundingSources, 'id');

  const valueRenderer = (option: FormSelectNewOption<string>) => {
    const selectedFundingSource = indexedFundingSources[option.value];
    if (!selectedFundingSource) {
      return null;
    }

    return (
      <Container data-testid="funding-source-selected-value" paddingTop="s" paddingBottom="s" paddingRight="s">
        <FundingSourceCard fundingSource={selectedFundingSource} isSelectedOption={false} isSelectedValue />
      </Container>
    );
  };

  const optionRenderer = (option: FormSelectNewOption<string>, isSelected: boolean) => {
    if (option.value === ADD_NEW_FS_VALUE) {
      return (
        <Text data-testid="add-new-fs-option" color="brand.main">
          {option.label}
        </Text>
      );
    }

    const selectedFundingSource = indexedFundingSources[option.value];
    if (!selectedFundingSource) {
      return null;
    }

    return (
      <FundingSourceCard fundingSource={selectedFundingSource} isSelectedOption={isSelected} isSelectedValue={false} />
    );
  };

  const {
    props: searchProps,
    show: showSearch,
    filtered: filteredFundingSources,
  } = useFundingSourcesSearchBar(fundingSources);

  const newFundingSourceOption = {
    value: ADD_NEW_FS_VALUE,
    label: formatMessage('activities.paymentFlow.form.content.fundingSource.add-new'),
  };

  const filteredOptions = [
    ...filteredFundingSources.map((fundingSource) => ({
      value: fundingSource.id,
      label: fundingSource.displayName,
    })),
    newFundingSourceOption,
  ];

  const options = [
    ...fundingSources.map((fundingSource) => ({
      value: fundingSource.id,
      label: fundingSource.displayName,
    })),
    newFundingSourceOption,
  ];

  return (
    <Form.SelectNew
      {...registerField('fundingSourceId')}
      options={options}
      labelProps={{ label: formatMessage('activities.paymentFlow.form.content.fundingSource.label') }}
      valueRenderer={valueRenderer}
      optionRenderer={optionRenderer}
      searchBarProps={showSearch ? { ...searchProps, options: filteredOptions } : undefined}
      onChange={(event) => {
        if (event.target.value === ADD_NEW_FS_VALUE) {
          return onNewFundingSourceClick();
        }
        onChange(event.target.value);
      }}
      isLoading={isLoading}
      size="large"
      shouldHideClearButton
    />
  );
};
